import { useEffect, useState } from "react";
import { api, Constitution as CV, Manifest } from "../api";

export default function Constitution(_: { manifest: Manifest }) {
  const [current, setCurrent] = useState<CV | null>(null);
  const [versions, setVersions] = useState<CV[]>([]);
  const [viewing, setViewing] = useState<CV | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    try {
      const cur = await api.get<CV>("/api/firm/constitution");
      setCurrent(cur);
      setViewing(cur);
      setVersions(await api.get<CV[]>("/api/firm/constitution/versions"));
    } catch (e) { setError((e as Error).message); }
  }

  async function propose() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/firm/constitution/amend", { content: draft, note });
      setEditing(false);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Constitution</h1>
        {!editing && current && (
          <button className="btn btn-primary" onClick={() => { setDraft(current.content); setEditing(true); }}>
            Propose amendment
          </button>
        )}
      </div>
      {error && <div className="text-down text-sm">{error}</div>}

      {editing ? (
        <div className="card space-y-3">
          <div className="text-sm text-slate-400">
            The board will deliberate this amendment — the devil&rsquo;s advocate attacks it and the
            risk officer reviews it — before ratification. The full deliberation is recorded.
          </div>
          <textarea className="input font-mono h-96" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <input className="input" placeholder="Amendment note (why)" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="flex gap-2">
            <button className="btn btn-primary" onClick={propose} disabled={busy || !draft.trim()}>
              {busy ? "Board deliberating…" : "Submit to board"}
            </button>
            <button className="btn" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
          </div>
        </div>
      ) : viewing ? (
        <div className="card">
          <div className="flex items-center gap-3 mb-3 text-sm">
            <span className="badge bg-accent/20 text-accent">v{viewing.version}</span>
            <span className={`badge ${viewing.status === "ratified" ? "bg-up/20 text-up" : "bg-slate-700 text-slate-300"}`}>{viewing.status}</span>
            <span className="text-slate-500 font-mono text-xs">sha256:{viewing.content_hash.slice(0, 16)}…</span>
            {viewing.ratified_at && <span className="text-slate-500 text-xs">ratified {new Date(viewing.ratified_at).toLocaleString()}</span>}
          </div>
          <pre className="whitespace-pre-wrap text-sm text-slate-200 font-sans">{viewing.content}</pre>
        </div>
      ) : (
        <div className="card text-slate-500 text-sm">No constitution ratified yet.</div>
      )}

      {versions.length > 1 && (
        <div className="card">
          <h2 className="label">Version history</h2>
          <div className="flex flex-wrap gap-2">
            {versions.map((v) => (
              <button key={v.id}
                      className={`btn ${viewing?.id === v.id ? "border-accent text-accent" : ""}`}
                      onClick={() => setViewing(v)}>
                v{v.version} {v.status === "ratified" ? "●" : ""}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
